import { useAuth } from 'restaurantAdmin/context/AuthContext';
import { useRestaurant } from 'restaurantAdmin/context/useRestaurant';
import { useFeatureFlagContext } from 'restaurantAdmin/featureFlags';
import { NavigationMenu } from '@base-ui/react/navigation-menu';
import { Icon } from '@components/icon/Icon';
import cx from 'classnames';
import { groupBy } from 'lodash-es';
import {
  generatePath,
  Link,
  NavLink,
  useLocation,
  useSearchParams,
} from 'react-router-dom';
import typography from '~styles/typography.scss';
import { insertIf } from '@shared/utils/insertIf';
import { buildPathWithSearchParams } from '@shared/utils/urlUtils';
import {
  COVER_FLOW_PATH,
  GUEST_BOOK_GUEST_PATH,
  OPERATIONS_BULLETIN_BOARD_PATH,
  OPERATIONS_EVENTS_PATH,
  OPERATIONS_FLOOR_PLANS_PATH,
  OPERATIONS_INTEGRATIONS_HOTEL_SYNC_PATH,
  OPERATIONS_LISTINGS_PATH,
  OPERATIONS_MESSAGING_PATH,
  OPERATIONS_PRICING_GRID_PATH,
  OPERATIONS_RESERVATION_TIMELINE_PATH,
  OPERATIONS_SHIFTS_PATH,
  RESERVATIONS_SERVICE_PATH,
  SETTINGS_FINANCES_PATH,
  SETTINGS_GENERAL_PATH,
  SETTINGS_REPORTS_PATH,
  SETTINGS_TEAM_PATH,
  SETTINGS_VIP_PROGRAM_PATH,
} from '../paths';
import styles from './PrimaryNavigation.scss';

type SettingsMenuItem = {
  key: string;
  label: string;
  kind: 'link';
  path: string;
};

export const PrimaryNavigationTabs = () => {
  const { isManager } = useAuth();
  const { hotelIntegrationEnabled } = useRestaurant();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const { isEnabled } = useFeatureFlagContext();

  const showTheListingsPage = !isEnabled('hideTheListingsPage');

  const toWithSearchParams = (path: string) =>
    buildPathWithSearchParams(path, searchParams);

  const reservationsOperationsItems = [
    {
      label: 'Cover Flow',
      path: COVER_FLOW_PATH,
      group: 'Reservations',
    },
  ];

  if (isEnabled('reservationTimeline')) {
    reservationsOperationsItems.push({
      label: 'Timeline',
      path: OPERATIONS_RESERVATION_TIMELINE_PATH,
      group: 'Reservations',
    });
  }

  const setupOperationsItems = [
    {
      label: 'Floor Plans',
      path: OPERATIONS_FLOOR_PLANS_PATH,
      group: 'Setup',
    },
    {
      label: 'Shifts',
      path: OPERATIONS_SHIFTS_PATH,
      group: 'Setup',
    },
    {
      label: 'Pricing',
      path: OPERATIONS_PRICING_GRID_PATH,
      group: 'Setup',
    },
    {
      label: 'Events',
      path: OPERATIONS_EVENTS_PATH,
      group: 'Setup',
    },
  ];

  if (showTheListingsPage) {
    setupOperationsItems.push({
      label: 'Listings',
      path: OPERATIONS_LISTINGS_PATH,
      group: 'Setup',
    });
  }

  // Third-party systems Peak talks to. Hotel Sync (Opera) is the first; Lightspeed will be the
  // second (product decision: Operations > Integrations groups Hotel Sync with Lightspeed).
  // Manager-gated by operationsMenuItems below, like Setup.
  const integrationsOperationsItems = [
    {
      label: 'Hotel Sync',
      path: OPERATIONS_INTEGRATIONS_HOTEL_SYNC_PATH,
      group: 'Integrations',
    },
  ];

  const communicationOperationsItems = [
    {
      label: 'Bulletins',
      path: OPERATIONS_BULLETIN_BOARD_PATH,
      group: 'Communications',
    },
    {
      label: 'Message Templates',
      path: OPERATIONS_MESSAGING_PATH,
      group: 'Communications',
    },
  ];

  const operationsMenuItems = isManager
    ? [
        ...setupOperationsItems,
        ...insertIf(hotelIntegrationEnabled, integrationsOperationsItems),
        ...reservationsOperationsItems,
        ...communicationOperationsItems,
      ]
    : [...reservationsOperationsItems];

  const isOperationsMenuActive = operationsMenuItems.some((menuItem) =>
    pathname.includes(menuItem.path),
  );

  const operationsMenuItemsByGroup = groupBy(operationsMenuItems, 'group');

  const SETTINGS_MENU_ITEMS: SettingsMenuItem[] = [
    {
      key: 'general',
      label: 'General',
      kind: 'link',
      path: SETTINGS_GENERAL_PATH,
    },
    ...insertIf(isEnabled('vipProgramCodes') || isEnabled('vipProgramTiers'), [
      {
        key: 'vip-program',
        label: 'VIP Program',
        kind: 'link' as const,
        path: SETTINGS_VIP_PROGRAM_PATH,
      },
    ]),
    { key: 'team', label: 'Team', kind: 'link', path: SETTINGS_TEAM_PATH },
    {
      key: 'finances',
      label: 'Finances',
      kind: 'link',
      path: SETTINGS_FINANCES_PATH,
    },
    {
      key: 'reports',
      label: 'Reports',
      kind: 'link',
      path: SETTINGS_REPORTS_PATH,
    },
  ];

  const isSettingsMenuActive = SETTINGS_MENU_ITEMS.some((menuItem) =>
    pathname.includes(menuItem.path),
  );

  return (
    <NavigationMenu.Root key={pathname}>
      <NavigationMenu.List className={styles.navigationList}>
        <NavigationMenu.Item>
          <NavigationMenu.Link
            render={() => (
              <NavLink
                className={({ isActive }) =>
                  cx({
                    [typography.t2]: true,
                    [styles.menuButton]: true,
                    [styles.activeMenuButton]: isActive,
                  })
                }
                to={toWithSearchParams(RESERVATIONS_SERVICE_PATH)}
              >
                <span className={styles.iconsContainer}>
                  <Icon name={'tent'} className={styles.icon} />
                </span>
                Service
              </NavLink>
            )}
          />
        </NavigationMenu.Item>
        <NavigationMenu.Item>
          <NavigationMenu.Link
            render={() => (
              <NavLink
                className={({ isActive }) =>
                  cx({
                    [typography.t2]: true,
                    [styles.menuButton]: true,
                    [styles.activeMenuButton]: isActive,
                  })
                }
                to={toWithSearchParams(
                  generatePath(GUEST_BOOK_GUEST_PATH, { guestId: null }),
                )}
              >
                <span className={styles.iconsContainer}>
                  <Icon
                    name={'couple'}
                    className={cx(styles.icon, styles.couple)}
                  />
                </span>
                Guest Book
              </NavLink>
            )}
          />
        </NavigationMenu.Item>
        <NavigationMenu.Item>
          <NavigationMenu.Trigger
            className={cx({
              [typography.t2]: true,
              [styles.menuButton]: true,
              [styles.activeMenuButton]: isOperationsMenuActive,
            })}
          >
            <span className={styles.iconsContainer}>
              <NavigationMenu.Icon
                render={() => <Icon name="gear" className={styles.icon} />}
              />
              <NavigationMenu.Icon
                render={() => (
                  <Icon
                    name="chevron"
                    className={cx(styles.icon, styles.chevron)}
                  />
                )}
              />
            </span>
            Operations
          </NavigationMenu.Trigger>
          <NavigationMenu.Content className={styles.menuContent}>
            {Object.keys(operationsMenuItemsByGroup).map((group) => (
              <span className={styles.menuGroup} key={group}>
                <h3 className={typography.c3_20}>{group}</h3>
                <ul>
                  {operationsMenuItemsByGroup[group].map((item) => {
                    return (
                      <li key={item.path}>
                        <Link
                          className={cx(typography.t1, styles.link)}
                          to={toWithSearchParams(item.path)}
                        >
                          {item.label}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
                <hr />
              </span>
            ))}
          </NavigationMenu.Content>
        </NavigationMenu.Item>
        {isManager && (
          <NavigationMenu.Item>
            <NavigationMenu.Trigger
              className={cx({
                [typography.t2]: true,
                [styles.menuButton]: true,
                [styles.activeMenuButton]: isSettingsMenuActive,
              })}
            >
              <span className={styles.iconsContainer}>
                <NavigationMenu.Icon
                  render={() => <Icon name="wrench" className={styles.icon} />}
                />
                <NavigationMenu.Icon
                  render={() => (
                    <Icon
                      name="chevron"
                      className={cx(styles.icon, styles.chevron)}
                    />
                  )}
                />
              </span>
              Settings
            </NavigationMenu.Trigger>
            <NavigationMenu.Content className={styles.menuContent}>
              <ul className={styles.GridLinkList}>
                {SETTINGS_MENU_ITEMS.map((item) => {
                  return (
                    <li key={item.key}>
                      <Link
                        className={cx(typography.t1, styles.link)}
                        to={toWithSearchParams(item.path)}
                      >
                        {item.label}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </NavigationMenu.Content>
          </NavigationMenu.Item>
        )}
      </NavigationMenu.List>
      <NavigationMenu.Portal>
        <NavigationMenu.Positioner
          className={styles.positioner}
          sideOffset={0}
          collisionPadding={{ top: 5, bottom: 5, left: 20, right: 20 }}
          collisionAvoidance={{ side: 'none' }}
        >
          <NavigationMenu.Popup>
            <NavigationMenu.Viewport />
          </NavigationMenu.Popup>
        </NavigationMenu.Positioner>
      </NavigationMenu.Portal>
    </NavigationMenu.Root>
  );
};
